import React, { useEffect, useState } from 'react';
import RiskIntakeV2 from './RiskIntakeV2';
import RiskLibrary from './RiskLibrary';
import RiskReports from './RiskReports';
import HubCorrespondence from '../Calendar/HubCorrespondence';
import RiskDetail from './RiskDetail';
import SimpleSurvey from './SimpleSurvey';
import SurveyBuilder from './SurveyBuilder';
import Assessments from './Assessments';
import AssessmentDetail from './AssessmentDetail';
import AppetiteSettings from './AppetiteSettings';
import ApprovalSettings from './ApprovalSettings';
import Workflows from './Workflows';
import ScoringMethodologySelector from '../../components/risk/ScoringMethodologySelector';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function RiskDashboard({ user } = {}) {
  const [activeSection, setActiveSection] = useState('intake');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; }
  })();
  const coreRole = sessionUser?.role;
  const riskInfo = sessionUser?.app_roles?.risk;
  const riskRole = riskInfo?.role;
  const riskDeptScoped = Boolean(riskInfo?.department_id);
  const isAdmin = coreRole === 'superadmin' || (riskRole === 'admin' && !riskDeptScoped);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await fetch(`${API_BASE}/risk-assessment/notifications/pending`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setNotifications(data.notifications || []);
      } catch (_) {}
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60_000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (nid) => {
    try {
      await fetch(`${API_BASE}/risk-assessment/notifications/${nid}/read`, { method: 'PUT', credentials: 'include' });
      setNotifications(prev => prev.filter(n => n.id !== nid));
    } catch (_) {}
  };

  const isComplianceLevel = !!user && (
    user.role === 'superadmin' || user.role === 'admin'
    || user.user_type === 'compliance_officer' || user.user_type === 'compliance_analyst'
    || user.can_see_privacy === true || user.is_compliance_team === true);

  // 3 core nav items
  const NAV = [
    { key: 'intake',       label: 'Intake',    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg> },
    { key: 'risk-library', label: 'Register',  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z"/></svg> },
    { key: 'reports',     label: 'Reports',   icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/></svg> },
    ...(isComplianceLevel ? [{ key: 'correspondence', label: 'Mail', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/></svg> }] : []),
  ];

  // Settings sub-items (admin only)
  const SETTINGS_NAV = [
    { key: 'assessments',       label: 'Annual Assessment' },
    { key: 'survey-builder',    label: 'Advanced Survey Builder' },
    { key: 'scoring',           label: 'Scoring Methodology' },
    { key: 'appetite',          label: 'Risk Appetite & Thresholds' },
    { key: 'approval-settings', label: 'Approval Settings' },
    { key: 'workflows',         label: 'Workflows & Schedules' },
  ];

  const renderContent = () => {
    if (selectedAssessment) {
      return <AssessmentDetail assessmentId={selectedAssessment.id} onBack={() => setSelectedAssessment(null)} />;
    }
    if (selectedRiskId) {
      return <RiskDetail riskId={selectedRiskId} onBack={() => setSelectedRiskId(null)} onOpenRisk={(id) => setSelectedRiskId(id)} />;
    }
    switch (activeSection) {
      case 'intake':            return <RiskIntakeV2 />;
      case 'risk-library':      return <RiskLibrary onOpenRisk={(id) => setSelectedRiskId(id)} />;
      case 'surveys':           return <SimpleSurvey />;
      case 'reports':           return <RiskReports />;
      case 'correspondence':    return (
        <div className="p-6 max-w-5xl">
          <h1 className="text-lg font-bold text-slate-800 tracking-tight mb-1">Correspondence</h1>
          <HubCorrespondence base="/api/risk-assessment/correspondence" chipFor={() => 'Risk'}
            intro="Every email sent from Workstream about a risk, with its follow-ups and replies in one thread. Reply here and it goes out the same way, into the same thread." />
        </div>
      );
      case 'assessments':       return <Assessments onAssessmentSelect={(a) => setSelectedAssessment(a)} />;
      case 'survey-builder':    return <SurveyBuilder />;
      case 'scoring':           return <ScoringMethodologySelector />;
      case 'appetite':          return <AppetiteSettings />;
      case 'approval-settings': return <ApprovalSettings />;
      case 'workflows':         return <Workflows />;
      default:                  return <RiskIntakeV2 />;
    }
  };

  const isSettingsItem = ['assessments', 'survey-builder', 'appetite', 'approval-settings', 'workflows'].includes(activeSection);

  return (
    <div className="h-screen flex bg-[#f8fafc]">

      {/* ═══ SIDEBAR — minimal, 3 items + settings ═══ */}
      <div className="w-16 flex-shrink-0 bg-[#0b0e14] border-r border-white/[0.04] flex flex-col items-center py-4">
        {/* Core nav */}
        {NAV.map(item => {
          const active = activeSection === item.key;
          return (
            <div key={item.key} className="relative group mb-1">
              <button
                onClick={() => { setActiveSection(item.key); setSelectedAssessment(null); setSelectedRiskId(null); setShowSettings(false); }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-100
                  ${active && !isSettingsItem ? 'bg-white/[0.1] text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]'}`}
                title={item.label}
              >
                {active && !isSettingsItem && <span className="absolute -left-[9px] w-[3px] h-5 bg-amber-400 rounded-r-full" />}
                {item.icon}
              </button>
              <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-900 text-white text-[11px] font-medium rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {item.label}
              </div>
            </div>
          );
        })}

        <div className="flex-1" />

        {/* Notifications */}
        <div className="relative mb-2">
          <button
            onClick={() => setShowNotifPanel(v => !v)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]`}
            title="Notifications"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/></svg>
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">{notifications.length}</span>
            )}
          </button>
        </div>

        {/* Settings gear (admin only) */}
        {isAdmin && (
          <button
            onClick={() => setShowSettings(v => !v)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all mb-1
              ${showSettings || isSettingsItem ? 'bg-white/[0.1] text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]'}`}
            title="Admin Settings"
          >
            {(showSettings || isSettingsItem) && <span className="absolute -left-[9px] w-[3px] h-5 bg-amber-400 rounded-r-full" />}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
          </button>
        )}
      </div>

      {/* ═══ SETTINGS PANEL — slides out from sidebar when gear is clicked ═══ */}
      {showSettings && isAdmin && (
        <div className="w-56 flex-shrink-0 bg-[#0f1219] border-r border-white/[0.04] flex flex-col py-4 px-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600 px-3 mb-3">Admin Settings</p>
          {SETTINGS_NAV.map(item => {
            const active = activeSection === item.key;
            return (
              <button key={item.key}
                onClick={() => { setActiveSection(item.key); setSelectedAssessment(null); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-[12px] font-medium transition-all mb-0.5
                  ${active ? 'bg-white/[0.07] text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.04]'}`}>
                {item.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header — compact */}
        <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-6 h-12 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-slate-800">Risk Management</span>
            <span className="text-slate-300">/</span>
            <span className="text-[13px] text-slate-500 capitalize">
              {activeSection === 'risk-library' ? 'Register' : activeSection.replace('-', ' ')}
            </span>
          </div>

          <div className="relative">
            {notifications.length > 0 && showNotifPanel && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-slate-800">Notifications</span>
                  <span className="text-[10px] text-slate-400">{notifications.length} new</span>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.slice(0, 6).map((n, i) => (
                    <button key={n.id || i}
                      onClick={() => { setShowNotifPanel(false); setSelectedAssessment({ id: n.assessment_id }); setActiveSection('assessments'); setShowSettings(true); markRead(n.id); }}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 transition-colors">
                      <p className="text-[12px] font-medium text-slate-800 truncate">{n.assessment_title}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">From {n.sender_name} · {new Date(n.sent_at).toLocaleDateString()}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-[#f8fafc]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
